import { QueryClient } from '@tanstack/react-query';

import { mapProduct, sanitizeTreezSearchParams } from '@/utils';
import { EnumCustomerType, OpenSearchProduct, TreezStore } from '@/types';

import { SearchService } from '../services';
import {
  buildQueryFilterFromSearchParams,
  buildSortFilterFromSearchParams,
} from './facets';
import { buildSearchBodyFromParams } from './transforms';

export async function getOpenSearchServerState({
  queryClient,
  searchParams,
  filters,
  store,
  customerType,
  includePromoItems = false,
  sort,
  revalidate,
}: {
  queryClient: QueryClient;
  searchParams: Record<string, string | string[] | boolean[]>;
  filters: Record<string, string | string[] | boolean[]>;
  store?: TreezStore;
  customerType?: EnumCustomerType;
  includePromoItems?: boolean;
  sort?: string;
  revalidate?: number;
}) {
  const sanitizedSearchParams = sanitizeTreezSearchParams({
    searchParams,
  });
  const queryFiltersByFacets = buildQueryFilterFromSearchParams(
    sanitizedSearchParams,
  );

  const fixedQueryFiltersByFacets = buildQueryFilterFromSearchParams(filters);
  const sortFilterFromSearchParams = buildSortFilterFromSearchParams(
    sanitizedSearchParams,
  );
  const defaultSortFilter = buildSortFilterFromSearchParams({
    sort: sort ?? '',
  });

  const { search } = new SearchService(
    store?.msoStoreEntityId,
    store?.msoOrgId,
    customerType,
    store?.strict_medical_enabled,
  );

  const { searchBody } = buildSearchBodyFromParams({
    searchParams: {
      ...(sort ? { sort } : {}),
      ...sanitizedSearchParams,
    },
    filters,
    includePromoItems,
  });

  const envCustomerType =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_MED_REC_SELECTION_ACTIVE === 'true'
      ? EnumCustomerType.ADULT
      : EnumCustomerType.NOT_SET;

  const searchResults = await search({ searchBody, revalidate });

  const definedKeysForPrefetch = [store?.shortName, customerType];
  const envDefinedKeysForPrefetch = [undefined, envCustomerType];

  await Promise.all(
    [definedKeysForPrefetch, envDefinedKeysForPrefetch].map(
      ([storeName, customerType]) =>
        queryClient.prefetchQuery({
          queryKey: [
            'products-search',
            storeName,
            customerType,
            JSON.stringify(searchBody),
          ],
          queryFn: async () => searchResults,
        }),
    ),
  );

  const products = searchResults.hits.hits.map(hit =>
    mapProduct(hit._source as OpenSearchProduct),
  );

  return {
    products,
    initialQueryFiltersByFacets: queryFiltersByFacets,
    initialFixedQueryFiltersByFacets: fixedQueryFiltersByFacets,
    // if sort is provided from config, we don't want to set it as initial sort to opensearch context
    // it will only be needed for an initial render
    defaultSortFilter,
    initialSortFilter: sortFilterFromSearchParams,
    initialSearchFilter: (searchParams.query as string) || '',
    initialPage: Math.max(
      parseInt((searchParams.page || '1') as string) - 1,
      0,
    ),
  };
}
